import isEqual from 'lodash/isEqual';

import { DASHBOARD_SYNC_POLL_INTERVAL_MS, DASHBOARD_SYNC_TIMEOUT_MS } from '@/config/constants';
import type { DashboardTab } from '@/features/dashboard/types';

import { fetchDashboardConfig, type DashboardConfig } from './dashboard.api';

export type DashboardConsistencyExpectation =
  | { type: 'upsert'; dashboardName: string; dashboard: DashboardTab; oldDashboardName?: string }
  | {
      type: 'delete';
      dashboardName: string;
      retainedDashboards?: DashboardConfig;
    }
  | { type: 'replace'; dashboards: DashboardConfig };

export class DashboardConsistencyTimeoutError extends Error {
  constructor(cause?: unknown) {
    super('Dashboard configuration did not reach the expected state before timing out.', {
      cause
    });
    this.name = 'DashboardConsistencyTimeoutError';
  }
}

export const isDashboardConsistencyTimeoutError = (
  error: unknown
): error is DashboardConsistencyTimeoutError => error instanceof DashboardConsistencyTimeoutError;

const createAbortError = (): Error => {
  const error = new Error('The operation was aborted.');
  error.name = 'AbortError';
  return error;
};

const throwIfAborted = (signal?: AbortSignal): void => {
  if (signal?.aborted) throw createAbortError();
};

const isAbortError = (error: unknown): boolean =>
  error instanceof Error && error.name === 'AbortError';

const waitForPollInterval = (delayMs: number, signal?: AbortSignal): Promise<void> =>
  new Promise((resolve, reject) => {
    const timeoutId = setTimeout(finish, delayMs);

    function cleanup() {
      clearTimeout(timeoutId);
      signal?.removeEventListener('abort', abort);
    }

    function finish() {
      cleanup();
      resolve();
    }

    function abort() {
      cleanup();
      reject(createAbortError());
    }

    if (signal?.aborted) {
      abort();
      return;
    }

    signal?.addEventListener('abort', abort, { once: true });
  });

const fetchConfigBeforeDeadline = async (
  fetchConfig: (signal?: AbortSignal) => Promise<DashboardConfig>,
  deadline: number,
  signal?: AbortSignal
): Promise<DashboardConfig> => {
  const fetchController = new AbortController();
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  let rejectOnAbort: (() => void) | undefined;

  const abortPromise = new Promise<never>((_, reject) => {
    rejectOnAbort = () => {
      fetchController.abort();
      reject(createAbortError());
    };
    signal?.addEventListener('abort', rejectOnAbort, { once: true });
  });

  const timeoutPromise = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(
      () => {
        reject(new DashboardConsistencyTimeoutError());
        fetchController.abort();
      },
      Math.max(0, deadline - performance.now())
    );
  });

  try {
    const dashboards = await Promise.race([
      fetchConfig(fetchController.signal),
      abortPromise,
      timeoutPromise
    ]);

    if (performance.now() >= deadline) throw new DashboardConsistencyTimeoutError();
    return dashboards;
  } finally {
    if (timeoutId !== undefined) clearTimeout(timeoutId);
    if (rejectOnAbort) signal?.removeEventListener('abort', rejectOnAbort);
  }
};

type DashboardConsistencyRead =
  | { dashboards: DashboardConfig; error?: never }
  | { dashboards?: never; error: unknown };

const readDashboardConfigForReconciliation = async (
  fetchConfig: (signal?: AbortSignal) => Promise<DashboardConfig>,
  deadline: number,
  signal: AbortSignal | undefined,
  previousReadError: unknown
): Promise<DashboardConsistencyRead> => {
  try {
    return {
      dashboards: await fetchConfigBeforeDeadline(fetchConfig, deadline, signal)
    };
  } catch (error) {
    if (isAbortError(error)) throw error;
    if (isDashboardConsistencyTimeoutError(error)) {
      throw previousReadError ? new DashboardConsistencyTimeoutError(previousReadError) : error;
    }
    return { error };
  }
};

const waitForNextConsistencyRead = async (
  deadline: number,
  intervalMs: number,
  signal: AbortSignal | undefined,
  cause: unknown
): Promise<void> => {
  const remainingMs = deadline - performance.now();
  if (remainingMs <= 0) throw new DashboardConsistencyTimeoutError(cause);
  await waitForPollInterval(Math.min(intervalMs, remainingMs), signal);
};

export const matchesDashboardExpectation = (
  dashboards: DashboardConfig,
  expectation: DashboardConsistencyExpectation
): boolean => {
  switch (expectation.type) {
    case 'upsert':
      return (
        isEqual(dashboards[expectation.dashboardName], expectation.dashboard) &&
        (expectation.oldDashboardName === undefined ||
          expectation.oldDashboardName === expectation.dashboardName ||
          !(expectation.oldDashboardName in dashboards))
      );
    case 'delete':
      return (
        !(expectation.dashboardName in dashboards) &&
        Object.entries(expectation.retainedDashboards ?? {}).every(([dashboardName, dashboard]) =>
          isEqual(dashboards[dashboardName], dashboard)
        )
      );
    case 'replace':
      return isEqual(dashboards, expectation.dashboards);
  }
};

export const waitForDashboardConsistency = async (
  expectation: DashboardConsistencyExpectation,
  options: {
    signal?: AbortSignal;
    fetchConfig?: (signal?: AbortSignal) => Promise<DashboardConfig>;
    intervalMs?: number;
    timeoutMs?: number;
  } = {}
): Promise<DashboardConfig> => {
  const {
    signal,
    fetchConfig = fetchDashboardConfig,
    intervalMs = DASHBOARD_SYNC_POLL_INTERVAL_MS,
    timeoutMs = DASHBOARD_SYNC_TIMEOUT_MS
  } = options;
  const deadline = performance.now() + timeoutMs;
  let lastReadError: unknown;

  while (true) {
    throwIfAborted(signal);

    const read = await readDashboardConfigForReconciliation(
      fetchConfig,
      deadline,
      signal,
      lastReadError
    );

    if ('error' in read) {
      lastReadError = read.error;
      await waitForNextConsistencyRead(deadline, intervalMs, signal, lastReadError);
      continue;
    }

    throwIfAborted(signal);

    if (matchesDashboardExpectation(read.dashboards, expectation)) return read.dashboards;
    await waitForNextConsistencyRead(deadline, intervalMs, signal, lastReadError);
  }
};
